import { apiClient, apiClientPaginated, ApiError } from '@/lib/api-client';
import type {
  AgingReport,
  ApplyPaymentInput,
  ApplyPaymentResult,
  CreateAccountInput,
  CreditAccount,
  CreditAccountListParams,
  CreditAccountListResult,
  CreditInvoice,
  CreditInvoiceListParams,
  CreditInvoiceListResult,
  LedgerParams,
  LedgerResult,
  UpdateAccountInput,
} from './types';

async function withFallback<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) {
      return fallback;
    }
    throw err;
  }
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')}`;
}

const EMPTY_ACCOUNT_LIST: CreditAccountListResult = { data: [], meta: { page: 1, limit: 20, total: 0, totalPages: 0 } };
const EMPTY_INVOICE_LIST: CreditInvoiceListResult = { data: [], meta: { page: 1, limit: 20, total: 0, totalPages: 0 } };
const EMPTY_LEDGER: LedgerResult = { data: { credit_account_id: '', customer_name: '', credit_limit: 0, outstanding_balance: 0, entries: [] }, meta: { page: 1, limit: 20, total: 0, totalPages: 0 } };

export const creditsApi = {
  accounts: (params: CreditAccountListParams) =>
    withFallback(
      () => apiClientPaginated<CreditAccountListResult['data']>(
        `/credit-accounts${buildQuery({ page: params.page, search: params.search, status: params.status })}`,
      ) as Promise<CreditAccountListResult>,
      EMPTY_ACCOUNT_LIST,
    ),

  account: (id: string) =>
    withFallback(() => apiClient<CreditAccount>(`/credit-accounts/${id}`), null),

  createAccount: (input: CreateAccountInput) =>
    apiClient<CreditAccount>('/credit-accounts', { method: 'POST', body: input }),

  updateAccount: (id: string, input: UpdateAccountInput) =>
    apiClient<CreditAccount>(`/credit-accounts/${id}`, { method: 'PATCH', body: input }),

  ledger: (id: string, params: LedgerParams) =>
    withFallback(
      // The ledger is a paginated envelope ({ data: { …, entries }, meta }) — apiClient
      // would keep only `data` and drop the meta the page reads.
      () => apiClientPaginated<LedgerResult['data']>(
        `/credit-accounts/${id}/ledger${buildQuery({ page: params.page, start_date: params.start_date, end_date: params.end_date })}`,
      ) as Promise<LedgerResult>,
      EMPTY_LEDGER,
    ),

  aging: (id: string) =>
    withFallback(() => apiClient<AgingReport>(`/credit-accounts/${id}/aging`), null),

  invoices: (params: CreditInvoiceListParams) =>
    withFallback(
      () => apiClientPaginated<CreditInvoiceListResult['data']>(
        `/credit-invoices${buildQuery({ page: params.page, account_id: params.account_id, status: params.status, location_id: params.location_id, date_from: params.date_from, date_to: params.date_to, customer_search: params.customer_search })}`,
      ) as Promise<CreditInvoiceListResult>,
      EMPTY_INVOICE_LIST,
    ),

  invoice: (id: string) =>
    withFallback(() => apiClient<CreditInvoice>(`/credit-invoices/${id}`), null),

  applyPayment: (invoiceId: string, input: ApplyPaymentInput) =>
    apiClient<ApplyPaymentResult>(`/credit-invoices/${invoiceId}/payments`, { method: 'POST', body: input }),
};
